import {
    AppFrame,
    IconUserCircle,
    ManageNavigation,
    PartnerPortalNavigation,
} from '@gmg/gmg-react-components';
import { getCurrentUser } from 'aws-amplify/auth';
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { COGNITO_CLIENT_OVERRIDE_STORAGE_KEY } from 'src/auth/cognitoClientOverride';
import { isGmgAdminRole, isGmgPartnerRole, isMemberRole, isOrganizationGroupAdminRole } from 'src/role';
import { organizationPaths, ROUTE_SEGMENTS, ROUTES, SYSADMIN_VIEW_ROUTES } from 'src/routes';
import { getCheckThirdPartyLicensingEvent, getEditUserNameEvent } from 'src/tracking';
import AppContext, { Language, LanguageArray, Role } from '../AppContext';
import GmgLogoDark from '../assets/svg/gmg_dark.svg';
import GmgLogoLight from '../assets/svg/gmg_light.svg';
import { useGetOrganization } from '../graphql/useGetOrganization';
import { AppProps } from './App';
import LoadingPage from './LoadingPage';
import ErrorMsg from './errorHandling/ErrorMsg';
import EditUserNameModalContainer from './member/EditUserNameModalContainer';

const ProtectedFrame: FunctionComponent<AppProps> = props => {
    const { t } = useTranslation();
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const shouldLoadOrgForSitesNav = !!params.orgId
        && context.featureFlags.showSitesPage
        && isOrganizationGroupAdminRole(context.user?.role);
    const { data: currentOrg } = useGetOrganization(shouldLoadOrgForSitesNav ? params.orgId! : '');
    const [isSignedIn, setIsSignedIn] = useState<boolean | undefined>();

    useEffect(() => {
        void (async () => {
            try {
                await getCurrentUser();
                setIsSignedIn(true);
            } catch (e: any) {
                setIsSignedIn(false);
            }
        })();
    }, []);

    const availableLanguages = useMemo(
        () =>
            new Map(
                LanguageArray.map((key: string) => [
                    key,
                    // t('PanelSettings.lang_en')
                    // t('PanelSettings.lang_de')
                    // t('PanelSettings.lang_ko')
                    // t('PanelSettings.lang_zh-Hant')
                    // t('PanelSettings.lang_zh')
                    // t('PanelSettings.lang_ja')
                    // t('PanelSettings.lang_es')
                    // t('PanelSettings.lang_pt')
                    // t('PanelSettings.lang_it')
                    // t('PanelSettings.lang_fr')
                    t(`PanelSettings.lang_${key}` as any) as string,
                ]),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [context.language],
    );

    if (isSignedIn === undefined) {
        return null;
    }

    const partnerPortalRouteMatcher = [
        {
            path: `/${ROUTE_SEGMENTS.services}/${ROUTE_SEGMENTS.partnerPortal}/*`,
        },
    ];

    const isPartnerPortalPath = (pathname: string) => !!matchRoutes(partnerPortalRouteMatcher, pathname);

    const isPartnerPortalEntry = isPartnerPortalPath(location.pathname);

    const createLoginNavigationTarget = (redirectTo: string) => {
        const searchParams = new URLSearchParams({ redirectTo });

        return {
            pathname: ROUTES.login,
            search: `?${searchParams.toString()}`,
        };
    };

    const shouldUsePartnerPortalDedicatedClient = isPartnerPortalEntry
        && !!context.featureFlags.usePartnerPortalDedicatedCognitoClient
        && !!context.partnerPortalIdentityProviderName
        && !!context.partnerPortalUserPoolWebClientId;

    const activeClientOverride = sessionStorage.getItem(COGNITO_CLIENT_OVERRIDE_STORAGE_KEY);
    const hasPartnerPortalDedicatedClientActivated = activeClientOverride === context.partnerPortalUserPoolWebClientId;

    if (shouldUsePartnerPortalDedicatedClient && !hasPartnerPortalDedicatedClientActivated) {
        const redirectTo = `${location.pathname}${location.search}`;

        return (
            <Navigate
                to={createLoginNavigationTarget(redirectTo)}
                state={{
                    redirectTo,
                    provider: context.partnerPortalIdentityProviderName,
                    clientId: context.partnerPortalUserPoolWebClientId,
                }}
                replace
            />
        );
    }

    const loginRequired = (
        !context.user
        && isSignedIn === false);

    if (loginRequired) {
        const redirectTo = `${location.pathname}${location.search}`;
        const provider = isPartnerPortalEntry ? context.partnerPortalIdentityProviderName : undefined;
        const clientId = isPartnerPortalEntry && context.featureFlags.usePartnerPortalDedicatedCognitoClient
            ? context.partnerPortalUserPoolWebClientId
            : undefined;

        return (
            <Navigate
                to={createLoginNavigationTarget(redirectTo)}
                state={{
                    redirectTo,
                    // Used for the login via Entra Identity Provider when the user is redirected from the GMG Partner Portal.
                    provider,
                    // Used to trigger partner-portal specific Cognito app client.
                    clientId,
                }}
                replace
            />
        );
    }

    // It takes some time until the user is loaded from the token.
    if (!context.user) {
        return <LoadingPage />;
    }

    const checkIfActive = (targetRoute: string) => location.pathname === targetRoute;

    const handleNavigate = (targetRoute: string) => {
        if (checkIfActive(targetRoute)) return;
        navigate(targetRoute);
    };

    const isSysadministrationView = (path: string, role: Role) => {
        const isGmgAdminInGmgView = isGmgAdminRole(role) && SYSADMIN_VIEW_ROUTES.includes(path);
        const isGmgAdminOnProfilePage = isGmgAdminRole(role) && path === ROUTES.profile;

        return isGmgAdminInGmgView || isGmgAdminOnProfilePage;
    };

    const organizationToRedirectTo = params.orgId || context.user.orgId;
    const currentOrgIsSite = !!currentOrg?.organization.isSiteOrganization;

    const getManageEntries = (): ManageNavigation['items'] => {
        if (!context.user || !context.user.orgId) return {};

        const myApps = { title: t('Layout.navItem_myApps_tab', 'My Apps'), path: ROUTES.profile };

        if (isMemberRole(context.user.role)) return { myApps };

        if (isSysadministrationView(location.pathname, context.user.role!)) {
            return {
                gmgAdmin: { title: t('Layout.navItem_GmgAdmin_tab', 'GMG Admin'), path: ROUTES.organizations },
                allUsers: { title: t('Layout.navItem_all_users_tab', 'All Users'), path: ROUTES.allUsers },
                gmgActivities: { title: t('Layout.navItem_gmg_activities_tab', 'GMG Activities'), path: ROUTES.gmgActivity },
                myApps,
            };
        }

        const showSites = context.featureFlags.showSitesPage
            && isOrganizationGroupAdminRole(context.user.role)
            && !currentOrgIsSite;

        return {
            organizationManagement: {
                title: t('Layout.navItem_OrgManagement_tab', 'Organization Management'),
                path: organizationPaths.root(organizationToRedirectTo!),
            },
            ...(showSites && {
                sites: {
                    title: t('Layout.navItem_Sites_tab', 'Sites'),
                    path: organizationPaths.sites(organizationToRedirectTo!),
                },
            }),
            userManagement: {
                title: t('Layout.navItem_UserManagement_tab', 'User Management'),
                path: organizationPaths.users(organizationToRedirectTo!),
            },
            licenseManagement: {
                title: t('Layout.navItem_LicenseManagement_tab', 'Licenses'),
                path: organizationPaths.systemLicences(organizationToRedirectTo!),
            },
            productsManagement: {
                title: t('Layout.navItem_ProductsManagement_tab', 'Products'),
                path: organizationPaths.products(organizationToRedirectTo!),
            },
            organizationActivities: {
                title: t('Layout.navItem_organization_activities_tab', 'Activity'),
                path: organizationPaths.activities(organizationToRedirectTo!),
            },
            // Related to MIO; hidden permanently but kept for the time being.
            ...(context.featureFlags.showDataAndAccessManagement && {
                dataManagement: {
                    title: t('Layout.navItem_DataManagement_tab', 'Data Management'),
                    path: organizationPaths.data(params.orgId!),
                },
            }),
            myApps,
        };
    };

    const handleEditUserNameClick = () => {
        context.trackEvent(getEditUserNameEvent());
        context.onShowModal('inputModal', {
            title: t('UserManagementActions.myAccount.modalTitle_title', 'My account'),
            titleIcon: <IconUserCircle />,
            content: <EditUserNameModalContainer
                onUserUpdate={props.handleUserUpdateAfterRefreshingToken}
            />,
        });
    };

    const isPartnerPortalNavigationVisible = context.featureFlags.showPartnerPortal && isGmgPartnerRole(context.user!.role);

    const getPartnerPortalNavigation = (): PartnerPortalNavigation | undefined => {
        if (!isPartnerPortalNavigationVisible) return undefined;

        const navigateToPartnerPortal = (path = '') => {
            const partnerPortalBaseUrl = context.partnerPortalBaseUrl.replace(/\/+$/, '');

            if (!partnerPortalBaseUrl) {
                console.warn('Partner portal base URL is not defined.');
                return;
            }

            window.location.assign(path ? `${partnerPortalBaseUrl}/${path}` : partnerPortalBaseUrl);
        };

        return {
            subtitle: t('Layout.partnerPortal_navigation_subtitle', 'GMG Partner'),
            onNavigate: navigateToPartnerPortal,
            items: {
                myCustomers: { title: t('Layout.navItem_partnerPortal_myCustomers_tab', 'My Accounts'), path: '' },
                contracts: { title: t('Layout.navItem_partnerPortal_contracts_tab', 'Contracts'), path: 'contract' },
                licenses: { title: t('Layout.navItem_partnerPortal_licenses_tab', 'Licenses'), path: 'license' },
                opportunities: { title: t('Layout.navItem_partnerPortal_opportunities_tab', 'Opportunities'), path: 'opportunities' },
                priceList: { title: t('Layout.navItem_partnerPortal_priceList_tab', 'Price List'), path: 'price-lists' },
                salesMaterials: { title: t('Layout.navItem_partnerPortal_salesMaterials_tab', 'Sales Material'), path: 'sales-materials' },
            },
        };
    };

    return (
        <AppFrame
            helpCenterUrl={context.language === 'de'
                ? 'https://customercare.gmgcolor.com/hc/de-de/articles/26519219416603-Was-ist-GMG-Manage'
                : 'https://customercare.gmgcolor.com/hc/en-us/articles/26519219416603-What-is-GMG-Manage'
            }
            manageNavigation={{
                items: getManageEntries(),
                currentPath: location.pathname,
                onNavigate: handleNavigate,
            }}
            partnerPortalNavigation={getPartnerPortalNavigation()}
            labels={{
                menu: {
                    myAccount: t('PanelSettings.myAccount', 'My account'),
                    preferences: t('PanelSettings.preferences_btn', 'Preferences'),
                    logout: t('PanelSettings.logout_btn', 'Logout'),
                    helpCenter: t('PanelSettings.helpCenter_link_label', 'Help center'),
                },
                preferences: {
                    version: t('Preferences.version', 'Version'),
                    language: t('Preferences.language', 'Language'),
                    mode: t('Preferences.mode', 'Mode'),
                    dark: t('Preferences.dark', 'Dark'),
                    light: t('Preferences.light', 'Light'),
                    thirdPartyNotices: {
                        title: t('Preferences.third_party_notices_link_label', 'Third-party notices for this version'),
                        staticPath: '/third-party-notices.html',
                    },
                    SBom: {
                        title: t('Preferences.sbom_link_label', 'Download Software Bill of Materials (SBOM)'),
                        staticPath: '/manage-license-sbom.json',
                    },
                },
            }}
            onThirdPartyNoticesClick={() => { context.trackEvent(getCheckThirdPartyLicensingEvent()); }}
            modal={props.modal}
            signOut={props.signOut}
            userName={`${context.user?.firstName} ${context.user?.lastName}`}
            userEmail={context.user?.email}
            activeTheme={props.theme}
            onChangeTheme={props.onChangeTheme}
            onMyAccountClick={handleEditUserNameClick}
            availableLanguages={availableLanguages}
            activeLanguage={context.language}
            onChangeLanguage={(language: string) =>
                props.onChangeLanguage(language as Language)
            }
            commitId={(window as any)._cdpAdminCommitId}
            appLogo={props.theme === 'dark'
                ? <GmgLogoDark width={78} height={28} />
                : <GmgLogoLight width={78} height={28} />}
        >
            {props.errorType
                ? <ErrorMsg errorType={props.errorType} />
                : <Outlet />}
        </AppFrame>
    );
};

export default ProtectedFrame;
